import { api } from "@verity/components";
import { useContext } from "react";
import {
	convertLiveSessionsToPendingSessions,
	LiveSessionWithInvitations,
	saveLiveSessions,
} from "../../../../../../../../packages/components/src/editQuiz/live/liveHelper";
import apiClient from "../../../../../helpers/api-client";
import { PortalControllerContext } from "../../../../context";

function stripSeconds(date: Date) {
	const newDate = new Date(date);
	newDate.setSeconds(0, 0);
	return newDate;
}

type SessionType = {
	title: string;
	start: Date;
	end: Date;
};

export default function useUpdateLiveSessions() {
	const { portalContext } = useContext(PortalControllerContext);
	const { instituteUuid, courseUuid } = portalContext;

	const fetchExistingLiveSessions = async (
		assessmentUuid: string | undefined,
	): Promise<LiveSessionWithInvitations[]> => {
		if (!assessmentUuid) {
			throw new Error("no assessmentUuids were obtained");
		}

		const sessions = (
			await apiClient.liveClient.getLiveSessionsWithUsers({
				instituteUuid,
				courseUuid,
				assessmentUuid,
			})
		).results;

		const invitations = Object.fromEntries(
			(
				await Promise.allSettled(
					sessions.map(async ({ id }) => [
						id,
						(await apiClient.liveClient.getLiveSessionInvitations(id)).filter(({ state }) => !state.cancelledAt),
					]),
				)
			)
				.filter((result) => result.status === "fulfilled")
				.map(
					(result) =>
						(
							result as PromiseFulfilledResult<
								[
									string,
									{
										liveSessionId: string;
										proctorGroupId: number;
									}[],
								]
							>
						).value,
				),
		);
		const sessionsWithInvitations = sessions.map((session) => ({
			...session,
			invitations: invitations[session.id] ?? [],
		}));

		return sessionsWithInvitations;
	};

	return async function updateLiveSessions(newSession: SessionType, assessmentUuid: string) {
		if (!instituteUuid || !courseUuid || !assessmentUuid) throw new Error("Missing UUIDs");

		const sessions = await fetchExistingLiveSessions(assessmentUuid);

		//There should only be one session per assessment / interview
		if (sessions.length > 1) throw new Error("This interview has too many sessions");

		const session = sessions[0];
		const existingLiveSessions = session ? { [session.id]: session } : {};

		const oldSessionPending = session ? convertLiveSessionsToPendingSessions([session])[session.id] : {};
		const newSessionPending = {
			name: newSession.title,
			startTime: stripSeconds(newSession.start),
			endTime: stripSeconds(newSession.end),
			capacity: 1,
			isInterview: true,
		};

		const sessionId = session?.id || "#0";

		const pendingLiveSessions = {
			[sessionId]: {
				identifier: sessionId,
				...oldSessionPending,
				...newSessionPending,
			},
		};

		await saveLiveSessions({
			api: api,
			assessmentUuids: {
				instituteUuid,
				courseUuid,
				assessmentUuid,
			},
			existingSessions: existingLiveSessions,
			pendingSessions: pendingLiveSessions,
		});

		const newSessionObjects = await fetchExistingLiveSessions(assessmentUuid);
		//There should only be one session per assessment / interview
		if (newSessionObjects.length !== 1) throw new Error("This interview has too many sessions");

		return newSessionObjects[0].id;
	};
}
